<script setup lang="ts">
import { inject, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue';

import { type IdRef, NavTabsSimpleParentKey } from './injection';
import { useGenerateIdRef } from './nav-tabs';

const props = defineProps<{
    active?: boolean;
    hidden?: boolean;
    disabled?: boolean;
    title?: string;
    idRef?: IdRef;
    manual?: boolean;
}>();

const emit = defineEmits<{
    (event: 'update:active', value: boolean): void;
    (event: 'activate', value: IdRef): void;
}>();

const generateIdRef = useGenerateIdRef();
const localIdRef = props.idRef ?? generateIdRef();
const localActive = ref(props.active);
const navTabsParent = inject(NavTabsSimpleParentKey, undefined);

function onActivate() {
    if(!props.manual) {
        localActive.value = true;
        emit('update:active', true);
        navTabsParent?.activateButton?.(localIdRef);
    }
    emit('activate', localIdRef);
}

watch(() => props.active, (val) => {
    if(!!unref(localActive) === !!val) return;
    localActive.value = val;
    if(val) {
        emit('activate', localIdRef);
        navTabsParent?.activateButton?.(localIdRef);
    }
});

watch(localActive, (val) => {
    if(!!props.active === !!val) return;
    emit('update:active', val);
});

onMounted(() => {
    navTabsParent?.registerButton?.({
        idRef: localIdRef,
        active: localActive,
    });
});

onBeforeUnmount(() => {
    navTabsParent?.unregisterButton?.(localIdRef);
});

</script>
<template>
    <li v-show="!props.hidden" role="presentation" :class="localActive ? 'active' : ''">
        <a
            :id="localIdRef.btnid"
            class="button-like rounded-3"
            :class="props.disabled ? 'disabled' : ''"
            role="tab"
            :aria-controls="localIdRef.tabid"
            :aria-selected="localActive || undefined"
            @click.prevent="onActivate"
        >
            <slot>
                {{ props.title }}
            </slot>
        </a>
    </li>
</template>
